import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'

import { APPLICATION_DRAWER_WIDTH, APPLICATION_RESOURCE } from '../Contracts/IApplication'

/** Звідки створюють заявку (`SPEC-020` §5.3). */
export type ApplicationCreateSource = 'request' | 'tender' | 'client' | 'carrier'

export interface OpenApplicationCreateParams {
    source: ApplicationCreateSource
    /** Значення картки джерела так, як їх тримає ядрова форма */
    record: Record<string, unknown>
    /** Заявку збережено — джерело перечитує свою картку й список */
    onSaved?: () => void
    /** Форму закрито (зі збереженням чи без) */
    onClosed?: () => void
}

const isPresent = (value: unknown): boolean =>
    value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)

/** Посилання для select-а: ядро впізнає вибране за `@id`, підпис — за `title`. */
const refOf = (value: unknown): Record<string, unknown> | null => {
    if (value === null || typeof value !== 'object') return null

    const record = value as Record<string, unknown>

    return typeof record['@id'] === 'string' || typeof record['uuid'] === 'string' ? record : null
}

/** Адреса рядком → масив адрес заявки; порожня не переноситься. */
const addressList = (value: unknown): string[] | null =>
    typeof value === 'string' && value.trim() !== '' ? [value.trim()] : null

/**
 * Передзаповнення форми заявки з джерела:
 *
 *  - **запит** (`SPEC-022` §5.5): `request`, клієнт, типи авто (ДП-08),
 *    `manager` ← відповідальний, опис (HTML), країни, вантаж, адреси рядком → масив;
 *  - **тендер** (`SPEC-023` §4.2): те саме без адрес — у тендера їх немає,
 *    заявка вимагає їх у формі;
 *  - **клієнт / перевізник** (вкладки CRM, легасі `ClientFormTemplate.jsx:186-204`,
 *    `CarrierFormTemplate.jsx:160-176`): лише сам власник.
 *
 * Користувач може змінити в формі будь-яке поле, зокрема клієнта (Q22.2).
 */
export const applicationCreateValues = (source: ApplicationCreateSource, record: Record<string, unknown>): Record<string, unknown> => {
    if (source === 'client' || source === 'carrier') {
        const owner = refOf(record)

        return owner ? { [source]: owner } : {}
    }

    const values: Record<string, unknown> = {}
    const self = refOf(record)

    if (self) values[source] = self

    const client = refOf(record['client'])
    const manager = refOf(record['responsible'])

    if (client) values['client'] = client
    if (manager) values['manager'] = manager

    for (const field of ['typesCars', 'loadingCountries', 'unloadingCountries', 'description', 'cargo']) {
        if (isPresent(record[field])) values[field] = record[field]
    }

    if (source === 'request') {
        const loading = addressList(record['addressLoading'])
        const unloading = addressList(record['addressUnloading'])

        if (loading) values['loadingAddresses'] = loading
        if (unloading) values['unloadingAddresses'] = unloading
    }

    return values
}

/**
 * Публічний Hook контейнера заявок (`SPEC-021` §8.9): відкриває створення
 * `Transportation.Application` з передзаповненням. Конвертація запиту чи
 * тендера відбувається на беку при збереженні форми з полем `request`/`tender`
 * (`SPEC-021` §5.9) — атомарно з заявкою.
 */
export const openApplicationCreate = ({ source, record, onSaved, onClosed }: OpenApplicationCreateParams): void => {
    OpenResourceCardTask({
        resource: APPLICATION_RESOURCE,
        mode: 'create',
        displayMode: 'drawer',
        drawer: { width: APPLICATION_DRAWER_WIDTH },
        initialValues: applicationCreateValues(source, record),
        ...(onSaved ? { onSave: async () => onSaved() } : {}),
        ...(onClosed ? { onClose: onClosed } : {}),
    })
}
